import { isNullOrUndefined as isNOU, detach, createElement } from '@syncfusion/ej2-base';
import { WordListConverter } from './ms-list-converter';
/**
 * Handles cleanup of pasted content for the Block Editor.
 */
export class ClipboardCleanupModule {
    constructor(manager) {
        this.parent = manager;
        this.listConverter = new WordListConverter(this);
    }
    /**
     * Handles the paste cleanup process.
     * It checks if the paste content is plain text or from MS Word and processes accordingly.
     *
     * @param {IPasteCleanupOptions} args - The arguments for paste cleanup.
     * @returns {string} - The cleaned HTML content.
     * @hidden
     */
    cleanupPaste(args) {
        const { html, plainText } = args;
        if (this.parent.pasteCleanupSettings.plainText) {
            return this.plainFormatting(plainText);
        }
        const isFromMsWord = this.isFromMsWord(html);
        if (isFromMsWord) {
            return this.cleanMsWordContent(html);
        }
        return this.cleanupHtml(html, this.parent.pasteCleanupSettings.keepFormat);
    }
    isFromMsWord(html) {
        return ClipboardCleanupModule.msWordPatterns.some((pattern) => pattern.test(html));
    }
    cleanMsWordContent(html) {
        const tempDiv = createElement('div');
        tempDiv.innerHTML = html;
        let listNodes = [];
        this.addListClass(tempDiv);
        listNodes = this.listCleanUp(tempDiv, listNodes);
        if (!isNOU(listNodes[0]) && listNodes[0].parentElement.tagName !== 'UL' &&
            listNodes[0].parentElement.tagName !== 'OL') {
            this.listConverter.convertListNodes(listNodes);
        }
        // Remove Word-specific elements
        this.removeUnwantedElements(tempDiv);
        // Clean up lists
        this.cleanupLists(tempDiv);
        // Process tables
        this.processTables(tempDiv);
        // Remove Word-specific classes
        this.removeWordClasses(tempDiv);
        // Clean up styles
        this.cleanupStyles(tempDiv, this.parent.pasteCleanupSettings.allowedStyles);
        // Remove empty elements
        this.removeEmptyElements(tempDiv);
        // Remove comments
        this.removeComments(tempDiv);
        // Process images
        this.processImages(tempDiv);
        return tempDiv.innerHTML;
    }
    addListClass(elm) {
        const allNodes = elm.querySelectorAll('*');
        for (let index = 0; index < allNodes.length; index++) {
            if (!isNOU(allNodes[index].getAttribute('style')) && allNodes[index].getAttribute('style').replace(/ /g, '').replace('\n', '').indexOf('mso-list:l') >= 0 &&
                allNodes[index].className.toLowerCase().indexOf('msolistparagraph') === -1 &&
                allNodes[index].tagName.charAt(0) !== 'H' && allNodes[index].tagName !== 'LI' &&
                allNodes[index].tagName !== 'OL' && allNodes[index].tagName !== 'UL') {
                allNodes[index].classList.add('msolistparagraph');
            }
        }
    }
    /* Cleans up HTML content and identifies list nodes for conversion */
    listCleanUp(containerElement, listNodes) {
        const nodesToRemove = [];
        let previousWasMsoList = false;
        const allElements = containerElement.querySelectorAll('*');
        for (let i = 0; i < allElements.length; i++) {
            const currentElement = allElements[i];
            // Check if element should be ignored
            if (this.shouldIgnoreElement(currentElement)) {
                nodesToRemove.push(currentElement);
                continue;
            }
            // Check if element is an MS Word list paragraph
            if (this.isMsoListParagraph(currentElement)) {
                // Add a null separator for new list if needed
                if (this.isFirstListItem(currentElement) && listNodes.length > 0 &&
                    listNodes[listNodes.length - 1] !== null) {
                    listNodes.push(null);
                }
                // Add the list node
                listNodes.push(currentElement);
            }
            // Add a null separator when transitioning from list to non-list block
            if (this.shouldAddListSeparator(previousWasMsoList, currentElement)) {
                listNodes.push(null);
            }
            // Update previous state flag for next iteration
            if (this.isBlockElement(currentElement)) {
                previousWasMsoList = this.isMsoListParagraph(currentElement);
            }
        }
        // Add a final null separator if needed
        if (listNodes.length > 0 && listNodes[listNodes.length - 1] !== null) {
            listNodes.push(null);
        }
        return listNodes;
    }
    /* Determines if an element should be ignored during cleanup */
    shouldIgnoreElement(element) {
        const isNotInIgnorableList = ClipboardCleanupModule.ignorableNodes.indexOf(element.nodeName) === -1;
        const isEmptyTextNode = element.nodeType === 3 && element.textContent.trim() === '';
        return isNotInIgnorableList || isEmptyTextNode;
    }
    /* Determines if an element is an MS Word list paragraph */
    isMsoListParagraph(element) {
        const elementClass = element.className;
        const hasClassName = elementClass && elementClass.toLowerCase().indexOf('msolistparagraph') !== -1;
        const elementStyles = element.getAttribute('style');
        const hasMsoListStyle = !isNOU(elementStyles) && elementStyles.indexOf('mso-list:') >= 0;
        return hasClassName && hasMsoListStyle;
    }
    /* Determines if an element is the first item in a list */
    isFirstListItem(element) {
        return element.className.indexOf('MsoListParagraphCxSpFirst') >= 0;
    }
    /* Determines if a list separator should be added */
    shouldAddListSeparator(previousWasMsoList, currentElement) {
        return previousWasMsoList &&
            this.isBlockElement(currentElement) && !this.isMsoListParagraph(currentElement);
    }
    /* Determines if an element is a block element */
    isBlockElement(element) {
        return ClipboardCleanupModule.blockNode.indexOf(element.nodeName.toLowerCase()) !== -1;
    }
    cleanupHtml(html, keepFormat) {
        const tempDiv = createElement('div');
        tempDiv.innerHTML = html;
        this.removeUnwantedElements(tempDiv);
        // Normalize and sanitize any tables in generic HTML (non-Word)
        this.processTables(tempDiv);
        if (!keepFormat) {
            this.deniedAttributes(tempDiv, true);
        }
        else if (this.parent.pasteCleanupSettings.deniedTags && this.parent.pasteCleanupSettings.deniedTags.length > 0) {
            this.deniedAttributes(tempDiv, false);
        }
        if (this.parent.pasteCleanupSettings.allowedStyles && this.parent.pasteCleanupSettings.allowedStyles.length > 0) {
            this.allowedStyle(tempDiv);
        }
        if (this.parent.pasteCleanupSettings.deniedTags && this.parent.pasteCleanupSettings.deniedTags.length > 0) {
            this.deniedTags(tempDiv);
        }
        this.removeEmptyElements(tempDiv);
        this.removeComments(tempDiv);
        this.processImages(tempDiv);
        this.cleanupCssPatterns(tempDiv);
        return tempDiv.innerHTML;
    }
    plainFormatting(html) {
        const tempDiv = createElement('div');
        tempDiv.innerHTML = html;
        this.detachInlineElements(tempDiv);
        this.getTextContent(tempDiv);
        this.removeEmptyElements(tempDiv);
        this.removeComments(tempDiv);
        return tempDiv.innerHTML;
    }
    removeUnwantedElements(element) {
        this.removeStyleElements(element);
        let innerElement = element.innerHTML;
        for (let i = 0; i < ClipboardCleanupModule.removableElements.length; i++) {
            // eslint-disable-next-line security/detect-non-literal-regexp
            const regExpStartElem = new RegExp('<' + ClipboardCleanupModule.removableElements[i] + '\\s*[^>]*>', 'g');
            // eslint-disable-next-line security/detect-non-literal-regexp
            const regExpEndElem = new RegExp('</' + ClipboardCleanupModule.removableElements[i] + '>', 'g');
            innerElement = innerElement.replace(regExpStartElem, '');
            innerElement = innerElement.replace(regExpEndElem, '');
        }
        element.innerHTML = innerElement;
    }
    removeStyleElements(element) {
        const styleElement = element.querySelector('style');
        if (!isNOU(styleElement)) {
            detach(styleElement);
        }
    }
    removeComments(element) {
        let innerElement = element.innerHTML;
        innerElement = innerElement.replace(/<!--[\s\S]*?-->/g, '');
        element.innerHTML = innerElement;
    }
    removeEmptyElements(element) {
        const emptyElements = element.querySelectorAll(':empty');
        for (let i = 0; i < emptyElements.length; i++) {
            if (emptyElements[i].tagName !== 'IMG' &&
                emptyElements[i].tagName !== 'BR' &&
                emptyElements[i].tagName !== 'IFRAME' &&
                emptyElements[i].tagName !== 'TD' &&
                emptyElements[i].tagName !== 'HR') {
                const detachableElement = this.findDetachEmptyElem(emptyElements[i]);
                if (!isNOU(detachableElement)) {
                    detach(detachableElement);
                }
            }
        }
    }
    findDetachEmptyElem(element) {
        let removableElement;
        if (!isNOU(element.parentElement)) {
            const hasNbsp = element.parentElement.textContent.length > 0 &&
                element.parentElement.textContent.match(/\u00a0/g) &&
                element.parentElement.textContent.match(/\u00a0/g).length > 0;
            if (!hasNbsp && element.parentElement.textContent.trim() === '' &&
                isNOU(element.parentElement.querySelector('img'))) {
                removableElement = this.findDetachEmptyElem(element.parentElement);
            }
            else {
                removableElement = element;
            }
        }
        else {
            removableElement = null;
        }
        return removableElement;
    }
    removeWordClasses(element) {
        const elementsWithClass = element.querySelectorAll('*[class]');
        for (let i = 0; i < elementsWithClass.length; i++) {
            const classList = elementsWithClass[i].classList;
            const classesToRemove = [];
            for (let j = 0; j < classList.length; j++) {
                if (classList[j].indexOf('Mso') === 0) {
                    classesToRemove.push(classList[j]);
                }
            }
            classesToRemove.forEach((className) => {
                elementsWithClass[i].classList.remove(className);
            });
            if (elementsWithClass[i].classList.length === 0) {
                elementsWithClass[i].removeAttribute('class');
            }
        }
    }
    cleanupStyles(element, allowedStyles) {
        const elementsWithStyle = element.querySelectorAll('*[style]');
        for (let i = 0; i < elementsWithStyle.length; i++) {
            const styleAttr = elementsWithStyle[i].getAttribute('style');
            if (!styleAttr) {
                continue;
            }
            const styles = styleAttr.split(';');
            let newStyles = '';
            for (let j = 0; j < styles.length; j++) {
                const style = styles[j].trim();
                if (!style) {
                    continue;
                }
                const [property, value] = style.split(':').map((s) => s.trim());
                if (property.indexOf('mso-') === 0) {
                    continue;
                }
                if (allowedStyles.indexOf(property) !== -1) {
                    newStyles += `${property}: ${value}; `;
                }
            }
            if (newStyles) {
                elementsWithStyle[i].setAttribute('style', newStyles.trim());
            }
            else {
                elementsWithStyle[i].removeAttribute('style');
            }
        }
    }
    processTables(element) {
        const tables = element.querySelectorAll('table');
        for (let i = 0; i < tables.length; i++) {
            this.sanitizeTableElement(tables[i]);
        }
    }
    sanitizeTableElement(table) {
        table.classList.add('e-blockeditor-table');
        // Remove unwanted attributes
        const attrs = ['border', 'cellpadding', 'cellspacing', 'width', 'height', 'style'];
        attrs.forEach((attr) => { if (table.hasAttribute(attr)) {
            table.removeAttribute(attr);
        } });
        // Remove colgroup/col sizing; our TableService will manage widths
        const colgroups = table.querySelectorAll('colgroup, col');
        for (let i = 0; i < colgroups.length; i++) {
            colgroups[i].parentElement.removeChild(colgroups[i]);
        }
        const cells = table.querySelectorAll('td, th');
        for (let j = 0; j < cells.length; j++) {
            const cell = cells[j];
            // Remove spans unsupported by editor
            cell.removeAttribute('rowspan');
            cell.removeAttribute('colspan');
            // Remove unwanted attributes/styles
            ['width', 'height', 'style'].forEach((attr) => { if (cell.hasAttribute(attr)) {
                cell.removeAttribute(attr);
            } });
            // Ensure minimal content wrapper
            if (cell.textContent.trim() === '' && !cell.querySelector('img') && !cell.querySelector('.e-table-cell-host')) {
                cell.innerHTML = '<p><br/></p>';
            }
        }
    }
    cleanupLists(element) {
        this.cleanList(element, 'UL');
        this.cleanList(element, 'OL');
        this.convertWordListParagraphs(element);
    }
    cleanList(element, listTag) {
        const replacableElem = element.querySelectorAll(`${listTag} div`);
        for (let j = replacableElem.length - 1; j >= 0; j--) {
            const parentElem = replacableElem[j].parentNode;
            while (replacableElem[j].firstChild) {
                parentElem.insertBefore(replacableElem[j].firstChild, replacableElem[j]);
            }
            const closestListElem = this.findClosestListElem(replacableElem[j]);
            if (closestListElem) {
                this.insertAfter(replacableElem[j], closestListElem);
            }
        }
    }
    findClosestListElem(listElem) {
        let closestListElem;
        while (!isNOU(listElem)) {
            listElem = !isNOU(listElem.closest('ul')) && listElem.tagName !== 'UL'
                ? listElem.closest('ul')
                : (listElem.tagName !== 'OL' ? listElem.closest('ol') : null);
            closestListElem = !isNOU(listElem) ? listElem : closestListElem;
        }
        return closestListElem;
    }
    insertAfter(newNode, referenceNode) {
        referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
    }
    convertWordListParagraphs(element) {
        const listParagraphs = element.querySelectorAll('p[style*="mso-list"]');
        if (listParagraphs.length === 0) {
            return;
        }
        let currentList = null;
        let currentListType = null;
        let currentLevel = 0;
        for (let i = 0; i < listParagraphs.length; i++) {
            const paragraph = listParagraphs[i];
            let listType = 'ul';
            const textContent = paragraph.textContent.trim();
            if (/^\d+\./.test(textContent) || /^[a-zA-Z]\./.test(textContent) || /^[ivxIVX]+\./.test(textContent)) {
                listType = 'ol';
            }
            let level = 1;
            const styleAttr = paragraph.getAttribute('style');
            if (styleAttr) {
                const levelMatch = styleAttr.match(/level(\d+)/);
                if (levelMatch) {
                    level = parseInt(levelMatch[1], 10);
                }
            }
            let content = paragraph.innerHTML;
            content = content.replace(/^[\s\u00A0]*(?:\d+\.|\w\.|\u2022|\u25CF|\u25CB|\u25A0|\u25A1)[\s\u00A0]+/, '');
            if (!currentList || currentListType !== listType || level < currentLevel) {
                currentList = document.createElement(listType);
                currentListType = listType;
                paragraph.parentNode.insertBefore(currentList, paragraph);
            }
            const listItem = document.createElement('li');
            listItem.innerHTML = content;
            currentList.appendChild(listItem);
            currentLevel = level;
            paragraph.parentNode.removeChild(paragraph);
        }
    }
    processImages(element) {
        const images = element.querySelectorAll('img');
        for (let i = 0; i < images.length; i++) {
            const img = images[i];
            img.classList.add('e-blockeditor-pasted-img');
            if (!img.hasAttribute('alt')) {
                img.setAttribute('alt', 'Pasted image');
            }
        }
    }
    cleanupCssPatterns(root) {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                const text = node.textContent.trim();
                const isInlineStyle = text.match(/^\s*(ol|ul|li)[\s\S]*?\{[\s\S]*?\}/i);
                return isInlineStyle ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
            }
        });
        const nodesToRemove = [];
        while (walker.nextNode()) {
            const node = walker.currentNode;
            nodesToRemove.push(node);
        }
        for (const node of nodesToRemove) {
            node.remove();
        }
    }
    deniedTags(element) {
        const deniedTags = this.parent.pasteCleanupSettings.deniedTags;
        for (let i = 0; i < deniedTags.length; i++) {
            const removableElements = element.querySelectorAll(deniedTags[i]);
            for (let j = removableElements.length - 1; j >= 0; j--) {
                const parentElem = removableElements[j].parentNode;
                while (removableElements[j].firstChild) {
                    parentElem.insertBefore(removableElements[j].firstChild, removableElements[j]);
                }
                parentElem.removeChild(removableElements[j]);
            }
        }
        return element;
    }
    deniedAttributes(element, clean) {
        const deniedAttrs = clean ? ['style', 'class'] : this.parent.pasteCleanupSettings.deniedTags;
        for (let i = 0; i < deniedAttrs.length; i++) {
            const elementsWithAttr = element
                .querySelectorAll(`[${deniedAttrs[i]}]`);
            for (let j = 0; j < elementsWithAttr.length; j++) {
                elementsWithAttr[j].removeAttribute(deniedAttrs[i]);
            }
        }
        return element;
    }
    allowedStyle(element) {
        const allowedStyles = this.parent.pasteCleanupSettings.allowedStyles;
        const styleElements = element.querySelectorAll('[style]');
        for (let i = 0; i < styleElements.length; i++) {
            let allowedStyleValue = '';
            const styleValue = styleElements[i].getAttribute('style').split(';');
            for (let j = 0; j < styleValue.length; j++) {
                const stylePair = styleValue[j].trim();
                if (!stylePair) {
                    continue;
                }
                const [property] = stylePair.split(':').map((s) => s.trim());
                if (allowedStyles.indexOf(property) !== -1) {
                    allowedStyleValue += stylePair + ';';
                }
            }
            styleElements[i].removeAttribute('style');
            if (allowedStyleValue) {
                styleElements[i].setAttribute('style', allowedStyleValue);
            }
        }
        return element;
    }
    detachInlineElements(element) {
        for (let i = 0; i < ClipboardCleanupModule.inlineNode.length; i++) {
            const inlineElements = element.querySelectorAll(ClipboardCleanupModule.inlineNode[i]);
            for (let j = 0; j < inlineElements.length; j++) {
                const parentElem = inlineElements[j].parentElement;
                if (!parentElem) {
                    continue;
                }
                while (inlineElements[j].firstChild) {
                    parentElem.insertBefore(inlineElements[j].firstChild, inlineElements[j]);
                }
                parentElem.removeChild(inlineElements[j]);
            }
        }
    }
    getTextContent(element) {
        for (let i = 0; i < ClipboardCleanupModule.blockNode.length; i++) {
            const blockElements = element.querySelectorAll(ClipboardCleanupModule.blockNode[i]);
            for (let j = 0; j < blockElements.length; j++) {
                const paragraph = document.createElement('p');
                paragraph.textContent = blockElements[j].textContent;
                blockElements[j].parentNode.replaceChild(paragraph, blockElements[j]);
            }
        }
        const allElements = element.querySelectorAll('*');
        for (let i = 0; i < allElements.length; i++) {
            const attributes = allElements[i].attributes;
            for (let j = attributes.length - 1; j >= 0; j--) {
                allElements[i].removeAttribute(attributes[j].name);
            }
        }
    }
}
ClipboardCleanupModule.inlineNode = ['a', 'abbr', 'acronym', 'audio', 'b', 'bdi', 'bdo', 'big', 'br', 'button',
    'canvas', 'cite', 'code', 'data', 'datalist', 'del', 'dfn', 'em', 'embed', 'font', 'i', 'iframe', 'img', 'input',
    'ins', 'kbd', 'label', 'map', 'mark', 'meter', 'noscript', 'object', 'output', 'picture', 'progress',
    'q', 'ruby', 's', 'samp', 'script', 'select', 'slot', 'small', 'span', 'strong', 'sub', 'sup', 'svg',
    'template', 'textarea', 'time', 'u', 'tt', 'var', 'video', 'wbr'];
ClipboardCleanupModule.ignorableNodes = ['A', 'APPLET', 'B', 'BLOCKQUOTE', 'BR',
    'BUTTON', 'CENTER', 'CODE', 'COL', 'COLGROUP', 'DD', 'DEL', 'DFN', 'DIR', 'DIV',
    'DL', 'DT', 'EM', 'FIELDSET', 'FONT', 'FORM', 'FRAME', 'FRAMESET', 'H1', 'H2',
    'H3', 'H4', 'H5', 'H6', 'HR', 'I', 'IMG', 'IFRAME', 'INPUT', 'INS', 'LABEL',
    'LI', 'OL', 'OPTION', 'P', 'PARAM', 'PRE', 'Q', 'S', 'SELECT', 'SPAN', 'STRIKE',
    'STRONG', 'SUB', 'SUP', 'TABLE', 'TBODY', 'TD', 'TEXTAREA', 'TFOOT', 'TH',
    'THEAD', 'TITLE', 'TR', 'TT', 'U', 'UL'];
/** List of HTML block node names */
ClipboardCleanupModule.blockNode = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'address', 'blockquote', 'button', 'center', 'dd', 'dir', 'dl', 'dt', 'fieldset',
    'frameset', 'hr', 'iframe', 'isindex', 'li', 'map', 'menu', 'noframes', 'noscript',
    'object', 'ol', 'pre', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'ul',
    'header', 'article', 'nav', 'footer', 'section', 'aside', 'main', 'figure', 'figcaption'];
ClipboardCleanupModule.removableElements = ['o:p', 'style', 'w:sdt', 'xml', 'script', 'meta', 'link'];
ClipboardCleanupModule.msWordPatterns = [
    /class='?Mso|style='[^ ]*\bmso-/i,
    /class="?Mso|style="[^ ]*\bmso-/i,
    /(class="?Mso|class='?Mso|class="?Xl|class='?Xl|class=Xl|style="[^"]*\bmso-|style='[^']*\bmso-|w:WordDocument)/gi,
    /style='mso-width-source:/i,
    // New Office 365 patterns
    /\bOutlineElement\b/i,
    /\bSCXW\d+\b/i,
    /\bBCX\d+\b/i,
    /\bTextRun\b/i,
    /\bEOP\b/i,
    /\bpara(id|eid)=/i,
    /data-ccp-/i,
    /\bWACImageContainer\b/i // Word's image container
];
